import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Snackbar,
  Link,
  Tooltip,
  Checkbox,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Code as GitLabIcon,
  DeleteSweep as BulkDeleteIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { projectsApi, parametersApi } from '../../services/api';
import ProjectForm from './ProjectForm';

const DESCRIPTION_LIMIT = 180;

function truncate(text, limit) {
  if (!text) return null;
  if (text.length <= limit) return text;
  return text.slice(0, limit).trimEnd() + '…';
}

function gitlabLabel(project) {
  if (!project.gitlab_url) return null;
  if (project.client_acronym && project.gitlab_issue_id) {
    return `${project.client_acronym} ${project.gitlab_issue_id}`;
  }
  if (project.client_acronym) return project.client_acronym;
  return 'GitLab';
}

function Projects() {
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchParameters();
  }, []);

  useEffect(() => {
    const targetId = location.state?.editProjectId;
    if (!targetId || loading) return;
    const project = projects.find((p) => p.id === targetId);
    if (project) handleEditProject(project);
  }, [loading, projects, location.state]);

  const fetchProjects = async () => {
    try {
      const response = await projectsApi.getAll();
      setProjects(response.data);
    } catch (err) {
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchParameters = async () => {
    try {
      const response = await parametersApi.getAll();
      setParameters(response.data);
    } catch (err) {
      console.error('Error fetching parameters:', err);
    }
  };

  const handleAddProject = () => {
    setSelectedProject(null);
    setOpenDialog(true);
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setOpenDialog(true);
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await projectsApi.delete(projectId);
        setProjects(projects.filter((p) => p.id !== projectId));
        setSelectedIds((prev) => { const next = new Set(prev); next.delete(projectId); return next; });
        setSnackbar({ open: true, message: 'Project deleted successfully', severity: 'success' });
      } catch {
        setSnackbar({ open: true, message: 'Failed to delete project', severity: 'error' });
      }
    }
  };

  const handleSaveProject = async (projectData) => {
    try {
      if (selectedProject) {
        const response = await projectsApi.update(selectedProject.id, projectData);
        setProjects(projects.map((p) => (p.id === selectedProject.id ? response.data : p)));
        setSnackbar({ open: true, message: 'Project updated successfully', severity: 'success' });
      } else {
        const response = await projectsApi.create(projectData);
        setProjects([...projects, response.data]);
        setSnackbar({ open: true, message: 'Project created successfully', severity: 'success' });
      }
      setOpenDialog(false);
    } catch {
      setSnackbar({ open: true, message: 'Failed to save project', severity: 'error' });
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === projects.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(projects.map((p) => p.id)));
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const res = await projectsApi.bulkDelete(ids);
      setProjects(projects.filter((p) => !selectedIds.has(p.id)));
      setSelectedIds(new Set());
      setSnackbar({ open: true, message: `${res.data.deleted} project(s) deleted`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete selected projects', severity: 'error' });
    } finally {
      setBulkDeleting(false);
      setConfirmBulkDelete(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'primary';
      case 'on_hold': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading projects…</Typography>
      </Box>
    );
  }

  const allSelected = projects.length > 0 && selectedIds.size === projects.length;
  const someSelected = selectedIds.size > 0;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" component="h1">
          Projects
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <Tooltip title={allSelected ? 'Deselect all' : 'Select all'}>
            <IconButton onClick={handleSelectAll} size="small">
              {allSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddProject}>
            Add Project
          </Button>
        </Box>
      </Box>

      {/* Bulk-action bar — appears when items are selected */}
      {someSelected && (
        <Paper
          variant="outlined"
          sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, mb: 2, bgcolor: 'primary.50' }}
        >
          <Typography variant="body2" fontWeight="medium">
            {selectedIds.size} project{selectedIds.size !== 1 ? 's' : ''} selected
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<BulkDeleteIcon />}
            onClick={() => setConfirmBulkDelete(true)}
          >
            Remove Selected
          </Button>
          <Button size="small" onClick={() => setSelectedIds(new Set())}>
            Clear selection
          </Button>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {projects.map((project) => {
          const label = gitlabLabel(project);
          const isSelected = selectedIds.has(project.id);
          return (
            <Grid item xs={12} md={6} lg={4} key={project.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  outline: isSelected ? '2px solid' : 'none',
                  outlineColor: 'primary.main',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Header row */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} gap={1}>
                    <Box display="flex" alignItems="flex-start" gap={0.5}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleSelect(project.id)}
                        size="small"
                        sx={{ mt: -0.5, ml: -1 }}
                      />
                      <Typography variant="h6" component="h2" sx={{ lineHeight: 1.3 }}>
                        {project.name}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={0.5} flexShrink={0}>
                      <Chip
                        label={project.status}
                        color={getStatusColor(project.status)}
                        size="small"
                      />
                    </Box>
                  </Box>

                  {/* Source indicators */}
                  <Box display="flex" gap={0.5} mb={1.5} flexWrap="wrap">
                    {project.source_type === 'email' && (
                      <Tooltip title="Created from forwarded email">
                        <Chip icon={<EmailIcon />} label="Email" size="small" variant="outlined" />
                      </Tooltip>
                    )}
                    {label && (
                      <Chip
                        icon={<GitLabIcon />}
                        label={label}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        component={Link}
                        href={project.gitlab_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                      />
                    )}
                  </Box>

                  {/* Truncated description */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5, whiteSpace: 'pre-line' }}
                  >
                    {truncate(project.description, DESCRIPTION_LIMIT) || 'No description'}
                  </Typography>

                  {project.required_skills && project.required_skills.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Required Skills
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {project.required_skills.map((skill, idx) => (
                          <Chip key={idx} label={skill} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {project.parameters && Object.keys(project.parameters).length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Parameters
                      </Typography>
                      {Object.entries(project.parameters).map(([key, value]) => (
                        <Typography key={key} variant="body2" color="text.secondary">
                          {key}: {value}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </CardContent>

                <CardActions>
                  <IconButton onClick={() => handleEditProject(project)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteProject(project.id)} size="small">
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {projects.length === 0 && !loading && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary">
            No projects found. Create your first project to get started.
          </Typography>
        </Box>
      )}

      {/* Edit / Add dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedProject ? 'Edit Project' : 'Add New Project'}
        </DialogTitle>
        <DialogContent>
          <ProjectForm
            project={selectedProject}
            parameters={parameters}
            onSave={handleSaveProject}
            onCancel={() => setOpenDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog open={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove {selectedIds.size} project{selectedIds.size !== 1 ? 's' : ''}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently remove the selected project{selectedIds.size !== 1 ? 's' : ''} from Now|Next|NotYet.
            GitLab issues are not affected. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkDelete(false)} disabled={bulkDeleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
          >
            {bulkDeleting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Projects;
